/**
 =========================================================
 * Material Dashboard 2 React - v2.2.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import * as React from 'react';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../examples/Tables/DataTable";
import data from "../dashboard/components/Projects/data";
import useApi from "../../components/Utils/api";
import MDInput from "../../components/MDInput";
import toast from "react-hot-toast";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import { useLocation } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useEffect } from "react";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import MerchantOfficeDetails from "./tabs/merchantOffficeDetails";
import MerchantBankDetails from "./tabs/merchantBankDetails";
import MerchantOtherDetails from "./tabs/merchantOtherDetails";
import MerchantEstablishmentDeclaration from "./tabs/merchantEstablishmentDeclaration";
import MerchantTerminals from "./tabs/terminals";
import MerchantUsers from "./tabs/users";
import MDAvatar from "../../components/MDAvatar";
import Icon from "@mui/material/Icon";
import MDConfirmAlert from "../../components/MDConfirmAlert";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { merchantSchema, fieldToTabMap } from "../../models/merchantModel";
import CustomTabPanel from '../../components/CustomTabPanel';
import PermissionControl from "../../components/Utils/PermissionControl";
import usePermission from "../../components/Utils/usePermission";
import {useTranslation} from "react-i18next";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
function Single() {
  const {t} = useTranslation();
  const {state} = useLocation();
  const [merchant, setMerchant] = React.useState({});
  const [merchantInner, setMerchantInner] = React.useState({});
  const [merchantbk, setMerchantbk] = React.useState({});
  // Flat tab set: 0 = Office Details, 1 = Bank Details, 2 = Other Details, 3 = Terminal, 4 = Users
  const [tabValue, setTabValue] = React.useState(0);
  const [editMode, setEditMode] = React.useState(false);
  const [userConfiguratorOpen, setUserConfiguratorOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState({});
  const [terminalConfiguratorOpen, setTerminalConfiguratorOpen] = React.useState(false);
  const [selectedTerminal, setSelectedTerminal] = React.useState({});
  const TERMINAL_TAB = 3;
  const USERS_TAB = 4;
  const isFormTab = (value) => value <= 2;
  // Terminals only apply to POS merchants (null = existing merchant without the flag, keep showing)
  const posEnabled = merchant?.merchantGatewayObject?.posEnabled !== false;
  const permission = usePermission();
  const { register, handleSubmit, reset, formState: { errors }, trigger, watch, control, setValue } = useForm({
    mode: "onChange",
    resolver: yupResolver(merchantSchema),
    defaultValues: merchantInner
  });
  const [requiredInfo, setRequiredInfo] = React.useState({
    cityList : [],
    referenceCompany : [],
    microAtm : []
  })

  const handleChange = (event, newValue) => {
    // Office/Bank/Other (0,1,2) are one continuous form — switching among
    // them keeps editMode and needs no confirmation. Crossing to/from Terminal
    // (3) or Users (4) is a different data view entirely, so it behaves like
    // the old outer-tab change: confirm if there are unsaved edits, reset editMode.
    const crossingUsersBoundary = isFormTab(tabValue) !== isFormTab(newValue);
    if (crossingUsersBoundary && editMode === true && (JSON.stringify(merchant) !== JSON.stringify(merchantbk))) {
      toast.custom((t) => (
        <MDConfirmAlert
          text={t('unsaved_changes_message')}
          toast={toast}
          t={t}
          confirmFunction={() => {
            toast.dismiss(t.id);
            updateMerchant();
            setTabValue(newValue);
            if(state != null && state.companyId){
              getSingleMerchant(state.companyId);
            }
          }}
          cancelFunction={() => {
            toast.dismiss(t.id);
            if(state != null && state.companyId){
              getSingleMerchant(state.companyId);
            }
            setEditMode(false);
            setTabValue(newValue);
          }}
        />
      ), {
        duration: Infinity,
      });
      return;
    }
    setTabValue(newValue);
    if (crossingUsersBoundary) {
      setEditMode(false);
    }
  };
  function convertNullToEmpty(obj) {
    for (let key in obj) {
      if (obj[key] === null || obj[key] === '') {
        obj[key] = '';
      } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        // Recursive call if the value is another object
        convertNullToEmpty(obj[key]);
      }
    }
    return obj;
  }

  const addNewUser = () => {
    setSelectedUser({});
    setUserConfiguratorOpen(true);
  };
  const addNewTerminal = () => {
    setSelectedTerminal({});
    setTerminalConfiguratorOpen(true);
  };
  const api = useApi();
  const getSingleMerchant = (companyId) => {
    const data = {
      companyId: companyId
    };
    api.post("merchant/single",data)
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          let result = response.data.body;
          convertNullToEmpty(result);
          setMerchant(result);
          setMerchantbk(result);
          setRequiredInfo({
            cityList : result.cityList,
            referenceCompany : result.referenceCompany,
            microAtm : result.microAtm
          });
          // result.merchant.mccCode = result.merchant.merchantCategoryCode.merchantCode;
          result.merchant.corporateTaxNumber = result.merchant.tinNo;
          if (Array.isArray(result.merchant.merchantBankDetails)) {
            result.merchant.merchantBankDetails.forEach(bankdetails => {
              if (bankdetails.accountNumber?.startsWith("AE")) {
                bankdetails.accountNumber = bankdetails.accountNumber.slice(2);
              }
            });
          }
          reset(result.merchant);
        } else {
          toast.error(response.data.error);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error(t('you_do_not_have_permission_to_perform_this_action'), { id: "PermissionError" });
          return;
        }
        console.log(error.response.data);
      })
  }
  const approveMerchant = () => {
    api.put("merchant/"+merchant.merchant.mid+"/approve")
        .then((response) => {
          if(response.data.statusCodeValue === 200) {
            toast.success(response.data.body.message);
            getSingleMerchant(merchant.merchant.companyId);
          } else {
            toast.error(response.data.error);
          }
        })
        .catch((error) => {
          console.log(error.response.data);
        })
  }
  const formSubmit = () => {
    handleSubmit(updateMerchant)();
  }
  const updateMerchant = (data) => {
    let formData = new FormData();

    if (data.supportingDocuments && data.supportingDocuments.length > 0) {
      data.supportingDocuments.forEach((file, index) => {
        for (let i = 0; i < file.length; i++) {
          formData.append('supportingDocuments', file[i]);
        }
      });
    }
    data.merchantBankDetails = data.merchantBankDetails.filter(bankdetails => bankdetails.currency !== ""
        && bankdetails.bankName !== ""
        && bankdetails.bankCode !== ""
        && bankdetails.branch !== ""
        && bankdetails.accountNumber !== "");
    data.merchantBankDetails.forEach(bankdetails => {
      bankdetails.accountNumber = "AE" + bankdetails.accountNumber;
    });
    delete data.supportingDocuments;
    if (data.merchantLogo) {
      formData.append('merchantLogo', data.merchantLogo);
    }
    delete data.merchantLogo;
    // Bank Fee / Commission Fee inputs are hidden from this form; always send a fixed value.
    data.bankFee = "0.0";
    data.issuerFee = "0.0";
    data.tinNo = data.corporateTaxNumber;
    delete data.corporateTaxNumber;
    if (merchant?.merchantGatewayObject?.cardProcessorType != null) {
      data.enabledCardProcessors = merchant.merchantGatewayObject.cardProcessorType;
    }
    if (merchant?.merchantGatewayObject?.enabledWalletTypes != null) {
      data.enabledWalletProcessors = merchant.merchantGatewayObject.enabledWalletTypes;
    }
    if (merchant?.merchantGatewayObject?.threeDSecureEnabled != null) {
      data.threeDSecureEnabled = merchant.merchantGatewayObject.threeDSecureEnabled;
    }
    if (merchant?.merchantGatewayObject?.saveCardEnabled != null) {
      data.saveCardEnabled = merchant.merchantGatewayObject.saveCardEnabled;
    }
    if (merchant?.merchantGatewayObject?.applePayEnabled != null) {
      data.applePayEnabled = merchant.merchantGatewayObject.applePayEnabled;
    }
    if (merchant?.merchantGatewayObject?.samsungPayEnabled != null) {
      data.samsungPayEnabled = merchant.merchantGatewayObject.samsungPayEnabled;
    }
    if (merchant?.merchantGatewayObject?.paymentGatewayEnabled != null) {
      data.paymentGatewayEnabled = merchant.merchantGatewayObject.paymentGatewayEnabled;
    }
    if (merchant?.merchantGatewayObject?.posEnabled != null) {
      data.posEnabled = merchant.merchantGatewayObject.posEnabled;
    }
    // Botim keys: only send when the user typed a replacement — blank means
    // "keep the existing stored keys" (the backend treats null as no change)
    if (merchant?.merchantGatewayObject?.botimPrivateKey?.trim()) {
      data.botimPrivateKey = merchant.merchantGatewayObject.botimPrivateKey.trim();
    }
    if (merchant?.merchantGatewayObject?.botimPublicKey?.trim()) {
      data.botimPublicKey = merchant.merchantGatewayObject.botimPublicKey.trim();
    }
    formData.append('merchantMaster', JSON.stringify(data));
    api.post("merchant/update", formData)
      .then((response) => {
        if(response.data.statusCodeValue === 200){
          setEditMode(false);
          toast.success(response.data.body.message);
          if(state != null && state.companyId){
            getSingleMerchant(state.companyId);
          }
          setEditMode(false);
          // window.location.reload();
        } else if (response.data.statusCodeValue === 409) {
          toast.error(response.data.body.message, { id: "MerchantUpdateError" });
        }
      })
      .catch((error)  => {
        cancelEditMode();
      });
  }
  const cancelEditMode = () => {
    if(state != null && state.companyId){
      getSingleMerchant(state.companyId);
    }
    setEditMode(false);
  }
  useEffect(() => {
    if(state != null && state.companyId){
      getSingleMerchant(state.companyId);
    }
  }, [state]);
  useEffect(() => {
    for (const field in errors) {
      if (fieldToTabMap[field] !== undefined) {
        setTabValue(fieldToTabMap[field]);
        break;
      }
    }
  }, [errors]);
  return (
    <DashboardLayout>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  {merchant?.merchant?.companyName || t('basic_info')}
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="secondary">
                  MID: {merchant?.merchant?.mid}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center">
                {isFormTab(tabValue) ? (
                  <>
                    {merchant.merchant != undefined && merchant?.merchant.status === "2" && (
                      <MDButton variant="outlined" color="info" onClick={() => approveMerchant()} style={{marginRight: "10px"}}>
                        <Icon fontSize="lg" style={{ marginRight: "2px" }}>check_circle</Icon>  {t('approve')}
                      </MDButton>
                    )}
                    {editMode === false &&
                        <PermissionControl permission={{ restrict: true, module: "Merchants", subModule: "Update" }}>
                        <MDButton variant="outlined" color="info" onClick={() => setEditMode(true)}>
                          <Icon fontSize="lg">mode-edit</Icon> {t('edit')}
                        </MDButton>
                        </PermissionControl>
                    }
                    {editMode === true && (
                      <>
                        <MDButton variant="outlined" color="info" onClick={() => cancelEditMode()} style={{marginRight: "16px"}}>
                          {t('cancel')}
                        </MDButton>
                        <MDButton variant="outlined" color="info" onClick={() => formSubmit()}>
                          {t('save')}
                        </MDButton>
                      </>
                    )}
                  </>
                ) : tabValue === TERMINAL_TAB ? (
                  <MDButton variant="gradient" color="info" onClick={() => addNewTerminal()}>
                    {t('add_terminal')}
                  </MDButton>
                ) : tabValue === USERS_TAB ? (
                  <PermissionControl permission={{ restrict: true, module: "User", subModule: "CreateMerchantUser" }}>
                    <MDButton variant="gradient" color="info" onClick={() => addNewUser()}>
                      {t('add_user')}
                    </MDButton>
                  </PermissionControl>
                ) : null}
              </MDBox>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Tabs
              value={tabValue}
              onChange={handleChange}
              aria-label="merchant detail tabs"
              scrollButtons
              allowScrollButtonsMobile
            >
              <Tab
                sx={{
                  fontSize: {
                    xxl: '1rem',
                    lg: '0.9rem',
                    md: '0.9rem',
                    sm: '0.8rem',
                    xs: '0.6rem'
                  }
                }}
                label="Office Details"
                {...a11yProps(0)} />
              <Tab
                sx={{
                  fontSize: {
                    xxl: '1rem',
                    lg: '0.9rem',
                    md: '0.9rem',
                    sm: '0.8rem',
                    xs: '0.6rem'
                  }
                }}
                label="Bank Details" {...a11yProps(1)} />
              <Tab
                sx={{
                  fontSize: {
                    xxl: '1rem',
                    lg: '0.9rem',
                    md: '0.9rem',
                    sm: '0.8rem',
                    xs: '0.6rem'
                  }
                }}
                label="Other Details" {...a11yProps(2)} />
              {posEnabled && (
              <Tab
                sx={{
                  fontSize: {
                    xxl: '1rem',
                    lg: '0.9rem',
                    md: '0.9rem',
                    sm: '0.8rem',
                    xs: '0.6rem'
                  }
                }}
                value={TERMINAL_TAB}
                label={t('terminal')} {...a11yProps(3)} />
              )}
              <Tab
                sx={{
                  fontSize: {
                    xxl: '1rem',
                    lg: '0.9rem',
                    md: '0.9rem',
                    sm: '0.8rem',
                    xs: '0.6rem'
                  }
                }}
                value={USERS_TAB}
                label={t('users')} {...a11yProps(4)} />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <FormProvider {...{ register, handleSubmit, reset, formState: { errors }, trigger, watch, requiredInfo, control, setValue }}>
              <CustomTabPanel value={tabValue} index={0} sx={{ p: 0 }}>
                <MerchantOfficeDetails merchant={merchant} editmode={editMode} setMerchant={setMerchant} type="update" />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1} sx={{ p: 0 }}>
                <MerchantBankDetails merchant={merchant} editmode={editMode} setMerchant={setMerchant} />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={2} sx={{ p: 0 }}>
                <MerchantOtherDetails merchant={merchant} editmode={editMode} setMerchant={setMerchant} type="update" />
              </CustomTabPanel>
            </FormProvider>
            {posEnabled && (
            <CustomTabPanel value={tabValue} index={3} sx={{ p: 0 }}>
              <MerchantTerminals
                merchant={merchant}
                openConfigurator={terminalConfiguratorOpen}
                setOpenConfigurator={setTerminalConfiguratorOpen}
                terminal={selectedTerminal}
                setTerminal={setSelectedTerminal}
                refreshMerchant={() => {
                  if (state != null && state.companyId) {
                    getSingleMerchant(state.companyId);
                  }
                }}
              />
            </CustomTabPanel>
            )}
            <CustomTabPanel value={tabValue} index={4} sx={{ p: 0 }}>
              <MerchantUsers
                merchant={merchant}
                openConfigurator={userConfiguratorOpen}
                setOpenConfigurator={setUserConfiguratorOpen}
                user={selectedUser}
                setUser={setSelectedUser}
                refreshMerchant={() => {
                  if (state != null && state.companyId) {
                    getSingleMerchant(state.companyId);
                  }
                }}
              />
            </CustomTabPanel>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Single;
